import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';

import { useIsMobile } from 'hooks/common';
import ModalBase from 'components/Modal/ModalBase';

import {
  FixedTicket,
  UnfixedTicket,
  BookingConnection,
  BookingItem,
  User,
  TicketState,
  ScheduleTerm,
} from 'types/api';

import { GET_BOOKING_LIST } from './graphQL';
import { useUserInformation } from 'graphQL/Zustand/useUserInformation';

import FixedBookingDetailContent from './DetailModal/FixedBookingDetailContent';
import UnfixedBookingDetailContent from './DetailModal/UnfixedBookingDetailContent';
import CancelBookingConfirmModal from './CancelBookingConfirmModal';
import { useMutationTicketsSale } from 'containers/SaleDetail/graphQL/hooks/useMutationTicketsSale';

interface BookingItemsFilter {
  page: number;
  size: number;
  filter: {
    hostId: string;
    customerSearchKeyword: string | null;
    scheduleId?: string;
    unscheduled?: boolean;
    // startedAt?: number;
    term?: {
      startedAt?: number;
      endedAt?: number;
    };
  };
}

interface Props {
  handleModal: () => void;
  handleRefetchBookingList: () => void;
  // item: UnfixedTicket | FixedTicket;
  item: UnfixedTicket | FixedTicket;
  refetchUnfixedTicketCount: () => void;
  idOrigin?: 'click' | 'url';
}

function DetailModal({
  handleModal,
  handleRefetchBookingList,
  item,
  refetchUnfixedTicketCount,
  idOrigin,
}: Props) {
  const isMobile = useIsMobile();

  const isFixedTicket = 'minimumQuota' in item ? true : false;

  const hostId = useUserInformation(state => state.userInfo.me.host.id);

  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [selectedConfirmedBookingIds, setSelectedConfirmedBookingIds] =
    useState<string[]>([]);

  const [isCancelBookingConfirmModal, setIsCancelBookingConfirmModal] =
    useState(false);

  const {
    handleRestoreTickets, // 예약 확정 취소 여러개 - 조율형
  } = useMutationTicketsSale();

  const [bookingItemsFilter, setBookingItemsFilter] =
    useState<BookingItemsFilter>({
      page: 1,
      size: 200,
      filter: {
        hostId: hostId,
        customerSearchKeyword: '',
        ...(!isFixedTicket
          ? {
              unscheduled: true,
              term: {
                startedAt: (item as UnfixedTicket).scheduleTerm.startedAt,
                endedAt: (item as UnfixedTicket).scheduleTerm.endedAt,
              },
              productIds: [
                (item as UnfixedTicket).bookingItem.booking.info.product.id,
              ],
              termTypes: ['STARTING'],
            }
          : {
              scheduleId: item.id,
            }),
      },
    });

  const {
    data: bookingItems,
    loading: bookingItemsLoading,
    refetch: bookingListRefetch,
  } = useQuery<{
    booking: { bookings: BookingConnection };
  }>(GET_BOOKING_LIST, {
    variables: {
      ...bookingItemsFilter,
      ...(!isFixedTicket
        ? {
            unscheduled: true,
            term: {
              startedAt: (item as UnfixedTicket).scheduleTerm.startedAt,
            },
            productIds: [
              (item as UnfixedTicket).bookingItem.booking.info.product.id,
            ],
          }
        : {
            scheduleId: item.id,
          }),
    },
    // notifyOnNetworkStatusChange: true,
  });

  const saveSelectedConfirmedBookingIds = (
    selectedConfirmedBookingIds: string[],
  ) => {
    setSelectedConfirmedBookingIds([...selectedConfirmedBookingIds]);
  };

  const handleCancelBookingConfirmModal = () => {
    setIsCancelBookingConfirmModal(prev => !prev);
  };

  const handleCancelBookingConfirm = () => {
    handleRestoreTickets(selectedConfirmedBookingIds, async () => {
      await bookingListRefetch();
      await handleRefetchBookingList();
      await refetchUnfixedTicketCount();
    });

    handleCancelBookingConfirmModal();
  };

  const keywordHandler = (value: string) => {
    setSearchKeyword(value);
  };

  const handleRefetchBookingItemList = async () => {
    await bookingListRefetch();
  };

  const handleSearch = async () => {
    if (bookingItemsFilter.filter.customerSearchKeyword === searchKeyword) {
      return;
    }

    await bookingListRefetch({
      ...bookingItemsFilter,
      filter: {
        ...bookingItemsFilter.filter,

        customerSearchKeyword:
          searchKeyword.length === 0 ? null : searchKeyword,
      },
    });

    setBookingItemsFilter(pre => ({
      ...pre,
      filter: {
        ...pre.filter,
        customerSearchKeyword:
          searchKeyword.length === 0 ? null : searchKeyword,
      },
    }));
  };

  return (
    <ModalBase
      isMobile={isMobile}
      handleModal={handleModal}
      useCustomHeader
      style={{ minHeight: isMobile ? '176px' : '748px' }}
    >
      {!isFixedTicket ? (
        <UnfixedBookingDetailContent
          isMobile={isMobile}
          handleModal={handleModal}
          item={item as UnfixedTicket}
          bookingItems={bookingItems?.booking?.bookings}
          handleRefetchBookingList={handleRefetchBookingList}
          searchedKeyword={bookingItemsFilter.filter.customerSearchKeyword}
          searchKeyword={searchKeyword}
          onSearch={handleSearch}
          keywordHandler={keywordHandler}
          bookingItemsLoading={bookingItemsLoading}
          handleRefetchBookingItemList={handleRefetchBookingItemList}
          handleCancelBookingConfirmModal={handleCancelBookingConfirmModal}
          saveSelectedConfirmedBookingIds={saveSelectedConfirmedBookingIds}
        />
      ) : (
        <FixedBookingDetailContent
          item={item as FixedTicket}
          handleModal={handleModal}
          bookingItems={bookingItems?.booking?.bookings}
          searchedKeyword={bookingItemsFilter.filter.customerSearchKeyword}
          searchKeyword={searchKeyword}
          onSearch={handleSearch}
          keywordHandler={keywordHandler}
          handleRefetchBookingList={handleRefetchBookingList}
          bookingItemsLoading={bookingItemsLoading}
          handleRefetchBookingItemList={handleRefetchBookingItemList}
          handleCancelBookingConfirmModal={handleCancelBookingConfirmModal}
          saveSelectedConfirmedBookingIds={saveSelectedConfirmedBookingIds}
        />
      )}

      {isCancelBookingConfirmModal && (
        <CancelBookingConfirmModal
          handleModal={handleCancelBookingConfirmModal}
          handleCancelBookingConfirm={handleCancelBookingConfirm}
        />
      )}
    </ModalBase>
  );
}

export default DetailModal;
